import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  GraduationCap,
  Home,
  Compass,
  Layers,
  Plus,
  Folder,
  X,
  BookOpen,
  MessageSquareText,
  StickyNote,
  Users,
  UserRoundSearch,
} from "lucide-react";
import { articlesAPI, communitiesAPI, userProfilesAPI } from "../services/api";
import { useToasts } from "../hooks/useToasts";
import useScreenWakeLock from "../hooks/useScreenWakeLock";
import LessonsCarousel from "../components/LessonsCarousel";
import Breadcrumbs from "../components/Breadcrumbs";
import StudyLayout from "../components/StudyLayout";
import NavigationButtons from "../components/NavigationButtons";
import { buildCommunitiesNavigationState } from "../utils/communitiesRouteCache";
import {
  buildDiscoverCommunitiesNavigationState,
  buildMeetPeopleNavigationState,
  mergeDiscoverRouteCache,
  readDiscoverRouteCache,
} from "../utils/discoverRouteCache";
import "./MySpace.css";

const MySpace = () => {
  useScreenWakeLock(true);

  const navigate = useNavigate();
  const location = useLocation();
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [hasFolders, setHasFolders] = useState(false);
  const [foldersChecked, setFoldersChecked] = useState(false);
  const { addToast } = useToasts();

  useEffect(() => {
    const checkFolders = async () => {
      try {
        const response = await articlesAPI.getFolders("lesson");
        setHasFolders(response.data.folders.length > 0);
      } catch {
        setHasFolders(false);
      } finally {
        setFoldersChecked(true);
      }
    };
    checkFolders();
  }, []);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      addToast("Folder name is required", "error-warning");
      return;
    }

    setShowCreateFolder(false);
    const folderName = newFolderName;
    setNewFolderName("");

    const toTitleCase = (str) =>
      str
        .trim()
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(" ");
    const finalFolderName = toTitleCase(folderName.trim());

    try {
      await articlesAPI.createArticle({
        title: finalFolderName,
        body: "Folder placeholder",
        contentType: "lesson",
      });
      addToast(`Folder "${finalFolderName}" created`, "success-info");
      navigate("/study/lessons");
    } catch {
      addToast("Failed to create folder", "error-warning");
    }
  };

  const handleOpenCommunities = React.useCallback(() => {
    navigate("/communities", {
      state: buildCommunitiesNavigationState(
        `${location.pathname}${location.search}${location.hash}`,
      ),
    });
  }, [location.hash, location.pathname, location.search, navigate]);

  const handleOpenDiscover = React.useCallback(async () => {
    const cachedState = buildDiscoverCommunitiesNavigationState();
    if (cachedState) {
      navigate("/discover-communities", { state: cachedState });
      return;
    }

    try {
      const response = await communitiesAPI.getDiscover();
      const discoverCommunities = response.data.communities || [];
      mergeDiscoverRouteCache({ discoverCommunities });
      navigate("/discover-communities", {
        state: { prefetchedDiscoverCommunities: discoverCommunities },
      });
      return;
    } catch {
      // Navigation should still work if the optimization fetch fails.
    }

    navigate("/discover-communities");
  }, [navigate]);

  const handleOpenMeetPeople = React.useCallback(async () => {
    const cachedState = buildMeetPeopleNavigationState();
    if (cachedState) {
      navigate("/meet-people", { state: cachedState });
      return;
    }

    try {
      const response = await userProfilesAPI.getDiscoverPeople();
      const discoverPeople = response.data.people || [];
      mergeDiscoverRouteCache({ discoverPeople });
      navigate("/meet-people", {
        state: { prefetchedPeople: discoverPeople },
      });
      return;
    } catch {
      // Navigation should still work if the optimization fetch fails.
    }

    navigate("/meet-people");
  }, [navigate]);

  const prefetchDiscoverRoutes = React.useCallback(async () => {
    const cached = readDiscoverRouteCache();
    const requests = [];

    if (!cached?.discoverCommunities?.length) {
      requests.push(
        communitiesAPI.getDiscover().then((response) => ({
          discoverCommunities: response.data.communities || [],
        })),
      );
    }

    if (!cached?.discoverPeople?.length) {
      requests.push(
        userProfilesAPI.getDiscoverPeople().then((response) => ({
          discoverPeople: response.data.people || [],
        })),
      );
    }

    if (!requests.length) return;

    const results = await Promise.allSettled(requests);
    const nextSnapshot = results.reduce((snapshot, result) => {
      if (result.status !== "fulfilled") return snapshot;
      return { ...snapshot, ...result.value };
    }, {});

    if (Object.keys(nextSnapshot).length) {
      mergeDiscoverRouteCache(nextSnapshot);
    }
  }, []);

  useEffect(() => {
    prefetchDiscoverRoutes();
  }, [prefetchDiscoverRoutes]);

  const studyMethods = [
    {
      id: "classes",
      title: "Classes",
      description: "Study method: Full page view",
      mobileLabel: "Reading",
      icon: GraduationCap,
      path: "/study/lessons",
      accentClass: "is-classes",
    },
    {
      id: "notes",
      title: "Notes",
      description: "Study method: Repetition",
      mobileLabel: "Repetition",
      icon: StickyNote,
      path: "/all-notes",
      accentClass: "is-notes",
    },
    {
      id: "flashcards",
      title: "Flashcards",
      description: "Study method: Cards",
      mobileLabel: "Cards",
      icon: Layers,
      path: "/all-flashcards",
      accentClass: "is-flashcards",
    },
    {
      id: "communities",
      title: "Communities",
      description: "Study groups and classrooms",
      mobileLabel: "Members",
      icon: Users,
      path: "/communities",
      accentClass: "is-communities",
    },
    {
      id: "discover",
      title: "Discover",
      description: "Find listed communities",
      mobileLabel: "Browse",
      icon: Compass,
      path: "/discover-communities",
      accentClass: "is-discover",
    },
    {
      id: "meet",
      title: "Meet People",
      description: "Find learners and connections",
      mobileLabel: "People",
      icon: UserRoundSearch,
      path: "/meet-people",
      accentClass: "is-meet",
    },
  ];

  return (
    <StudyLayout
      breadcrumbs={
        <Breadcrumbs
          path={[
            {
              label: (
                <span className="myspace-breadcrumb-label">
                  <Home size={14} />
                  <span>MySpace</span>
                </span>
              ),
              path: null,
            },
          ]}
        />
      }
      buttons={<NavigationButtons />}
    >
      <div className="">
        <div className="myspace-grid">
          {studyMethods.map((method) => {
            const Icon = method.icon;
            return (
              <div
                key={method.title}
                className={`myspace-card ${method.accentClass} ${
                  method.id === "classes" ? "myspace-card-has-overlay" : ""
                }`.trim()}
                onClick={() => {
                  if (method.id === "classes" && showCreateFolder) return;
                  if (method.id === "communities") {
                    handleOpenCommunities();
                    return;
                  }
                  if (method.id === "discover") {
                    handleOpenDiscover();
                    return;
                  }
                  if (method.id === "meet") {
                    handleOpenMeetPeople();
                    return;
                  }
                  navigate(method.path);
                }}
              >
                <div className="myspace-card-icon">
                  <Icon size={32} />
                </div>
                <div className="myspace-card-title">{method.title}</div>
                <div className="myspace-card-description">
                  {method.description}
                </div>
                <div className="myspace-card-description-mobile">
                  {method.mobileLabel}
                </div>
                {method.id === "classes" && (
                  <div className="myspace-card-actions">
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/all-lessons");
                      }}
                      title="View all lessons"
                      className="myspace-card-action-icon"
                    >
                      <BookOpen size={24} />
                    </div>
                    {hasFolders ? (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/content-lesson");
                        }}
                        title="Create new lesson"
                        className="myspace-card-action-icon"
                      >
                        <span className="myspace-card-action-subicon">
                          <BookOpen size={11} />
                        </span>
                        <Plus size={28} />
                      </div>
                    ) : !foldersChecked ? (
                      <div
                        aria-hidden="true"
                        className="myspace-card-action-icon myspace-card-action-placeholder"
                      />
                    ) : null}
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowCreateFolder(true);
                      }}
                      title="Create new folder"
                      className="myspace-card-action-icon"
                    >
                      <span className="myspace-card-action-subicon">
                        <Folder size={11} />
                      </span>
                      <Plus size={28} />
                    </div>
                  </div>
                )}
                {method.id === "notes" && (
                  <div className="myspace-card-actions">
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/all-notes");
                      }}
                      title="View all notes"
                      className="myspace-card-action-icon"
                    >
                      <BookOpen size={24} />
                    </div>
                    <div
                      className="myspace-card-action-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/content-note");
                      }}
                      title="Create new note"
                    >
                      <span className="myspace-card-action-subicon">
                        <StickyNote size={11} />
                      </span>
                      <Plus size={28} />
                    </div>
                  </div>
                )}
                {method.id === "flashcards" && (
                  <div className="myspace-card-actions">
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/all-flashcards");
                      }}
                      title="View all flashcards"
                      className="myspace-card-action-icon"
                    >
                      <BookOpen size={24} />
                    </div>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/content-flashcard");
                      }}
                      title="Create new flashcard"
                      className="myspace-card-action-icon"
                    >
                      <span className="myspace-card-action-subicon">
                        <Layers size={11} />
                      </span>
                      <Plus size={28} />
                    </div>
                  </div>
                )}
                {method.id === "communities" && (
                  <div className="myspace-card-actions">
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenCommunities();
                      }}
                      title="Open communities"
                      className="myspace-card-action-icon"
                    >
                      <Users size={24} />
                    </div>
                  </div>
                )}
                {method.id === "discover" && (
                  <div className="myspace-card-actions">
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDiscover();
                      }}
                      title="Discover communities"
                      className="myspace-card-action-icon"
                    >
                      <Compass size={24} />
                    </div>
                  </div>
                )}
                {method.id === "meet" && (
                  <div className="myspace-card-actions">
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenMeetPeople();
                      }}
                      title="Meet People"
                      className="myspace-card-action-icon"
                    >
                      <UserRoundSearch size={24} />
                    </div>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/connections");
                      }}
                      title="Connections"
                      className="myspace-card-action-icon"
                    >
                      <MessageSquareText size={24} />
                    </div>
                  </div>
                )}
                {method.id === "classes" && showCreateFolder && (
                  <div className="subfolder-create-in-card myspace-folder-creator">
                    <div className="delete-confirmation-content">
                      <textarea
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        placeholder="Subfolder name"
                        maxLength="80"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleCreateFolder();
                          }
                          if (e.key === "Escape") {
                            setShowCreateFolder(false);
                            setNewFolderName("");
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        rows="1"
                        className="folder-name-textarea myspace-folder-textarea"
                        onInput={(e) => {
                          e.target.style.height = "auto";
                          e.target.style.height = e.target.scrollHeight + "px";
                        }}
                      />
                      <div className="delete-actions myspace-folder-actions">
                        <button
                          className="create-confirm-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCreateFolder();
                          }}
                          title="Create"
                        >
                          <Plus size={14} />
                        </button>
                        <button
                          className="delete-cancel-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowCreateFolder(false);
                            setNewFolderName("");
                          }}
                          title="Cancel"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <LessonsCarousel />
      </div>
    </StudyLayout>
  );
};

export default MySpace;
